/*
 * Reusable free-gift trigger classes — attach either class to ANY element
 * (button, link, div) and this handles the click. Delegated on document, so it
 * works for markup added after page load (Nosto injections, cart re-renders…).
 *
 *   .js-open-free-product-popup  → if the cart already qualifies, opens the normal
 *                                  in-cart free-gift selector; otherwise opens the
 *                                  #free-gifts-popup showcase (a grid of the gifts).
 *   .js-open-free-gift-selector  → always opens the cart and reveals the picker.
 *
 * Depends on globals defined in custom.js (openPopup, updateCart). This file is
 * loaded after custom.js in layout/theme.liquid, and everything is looked up at
 * click time, so it degrades quietly if either is unavailable.
 */
(function () {
  // Reveal the cart's free-gift selector: open the cart, then show the picker.
  // Exposed on window so other code can trigger it directly, not only via a click.
  function openFreeGiftSelector() {
    if (typeof window.updateCart === 'function') {
      window.updateCart(); // opens + renders the cart
    }
    // The cart re-renders async, so poll briefly for the (fresh) selector, then reveal it.
    setTimeout(function () {
      var tries = 0;
      var iv = setInterval(function () {
        var selector = document.getElementById('free-gift-selector');
        var opener = document.getElementById('open-free-gift-selector');
        if (selector) {
          if (opener) {
            opener.classList.add('free-gift-opened');
          }
          selector.style.display = 'block';
          clearInterval(iv);
        } else if (++tries > 20) {
          clearInterval(iv);
        }
      }, 150);
    }, 400);
  }
  window.openFreeGiftSelector = openFreeGiftSelector;

  // Format pence using the shop money_format (handles the common {{amount}} /
  // {{amount_no_decimals}} tokens), stripping any trailing currency code.
  function formatMoney(cents, fmt) {
    fmt = fmt || '£{{amount}}';
    var m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = m ? m[1] : 'amount';
    var n = cents / 100;
    var amount = key.indexOf('no_decimals') >= 0 ? String(Math.round(n)) : n.toFixed(2);
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, amount).replace(/\s*[A-Z]{3}\s*$/, '');
  }

  // Read the threshold (pence) the popup was rendered with, defaulting to £40.
  function popupThreshold(popup) {
    var ctx = popup && popup.querySelector('[data-fg-context]');
    return (ctx && parseInt(ctx.getAttribute('data-threshold'), 10)) || 4000;
  }

  // Fill the popup's context strip from the LIVE cart, so it always reflects the
  // current value wherever the popup is opened from (PDP, collection, cart, etc.).
  // Mirrors the GWP widget's own state messaging. Runs on each open.
  function populatePopupContext(popup, cart) {
    var ctx = popup.querySelector('[data-fg-context]');
    if (!ctx) return;
    var head = ctx.querySelector('[data-fg-context-headline]');
    var sub = ctx.querySelector('[data-fg-context-sub]');
    var fill = ctx.querySelector('[data-fg-context-fill]');
    var meter = ctx.querySelector('[data-fg-context-meter]');
    var THRESHOLD = parseInt(ctx.getAttribute('data-threshold'), 10) || 4000;
    var GIFT_VALUE = parseInt(ctx.getAttribute('data-gift-value'), 10) || 2000;
    var FMT = ctx.getAttribute('data-money-format') || '£{{amount}}';
    var money = function (c) { return formatMoney(c, FMT); };

    var qualifying = 0, hasGift = false;
    (cart.items || []).forEach(function (item) {
      qualifying += item.final_line_price; // every line counts toward the threshold
      if (item.properties && '_cg' in item.properties) hasGift = true; // a free gift
    });
    if (fill) fill.style.width = Math.max(0, Math.min(100, (qualifying / THRESHOLD) * 100)) + '%';
    // meter (£X of £40) — only while progressing, like the widget
    if (meter) meter.textContent = (qualifying > 0 && qualifying < THRESHOLD) ? (money(qualifying) + ' of ' + money(THRESHOLD)) : '';
    if (qualifying >= THRESHOLD) {
      if (head) head.textContent = hasGift ? 'Free product added — it’s yours' : 'Free product unlocked';
      if (sub) sub.textContent = hasGift ? 'It’ll show as free at checkout.' : 'Worth up to ' + money(GIFT_VALUE);
    } else if (qualifying <= 0) {
      if (head) head.textContent = 'Spend ' + money(THRESHOLD) + ', get a FREE product';
      if (sub) sub.textContent = 'Worth up to ' + money(GIFT_VALUE);
    } else {
      if (head) head.textContent = 'You’re ' + money(THRESHOLD - qualifying) + ' away from a FREE product';
      if (sub) sub.textContent = 'Worth up to ' + money(GIFT_VALUE);
    }
  }

  // "See the free gifts": if the cart already qualifies, take them straight to the
  // normal in-cart free-gift selector so they can pick; otherwise open the showcase
  // popup (with its live context strip) so they can see what they're working toward.
  function openBrowseOrSelector() {
    var popup = document.querySelector('#free-gifts-popup');
    var THRESHOLD = popupThreshold(popup);

    function showPopup(cart) {
      if (!popup) return;
      if (cart) populatePopupContext(popup, cart);
      if (typeof window.openPopup === 'function') window.openPopup(popup);
    }

    (window.OB && window.OB.getCart
      ? window.OB.getCart()
      : fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }))
      .then(function (cart) {
        var qualifying = 0;
        (cart.items || []).forEach(function (item) { qualifying += item.final_line_price; });
        if (qualifying >= THRESHOLD) {
          openFreeGiftSelector();
        } else {
          showPopup(cart);
        }
      })
      .catch(function () { showPopup(null); }); // cart fetch failed → just show the popup
  }

  // The trigger classes can sit on a whole banner (outer container), not just a
  // button. When they do, the banner's gift-preview slider lives inside the
  // trigger — and its arrows/drag must keep working, not open the popup. So if a
  // click lands on a theme slider (.ob-slider-container) contained by the trigger,
  // leave it to the slider (which has its own click handlers).
  function onSlider(event, trigger) {
    var sld = event.target.closest('.ob-slider-container');
    return sld && trigger.contains(sld);
  }

  document.addEventListener('click', function (event) {
    // "See the free gifts" — route to the selector when qualified, else the popup.
    // If the trigger carries data-free-product-url, navigate there instead — lets
    // a banner point "browse the free products" at a real page when one is set.
    var browseEl = event.target.closest('.js-open-free-product-popup');
    if (browseEl) {
      if (onSlider(event, browseEl)) {
        return;
      }
      var url = browseEl.getAttribute('data-free-product-url');
      if (url) {
        window.location.href = url;
        return;
      }
      openBrowseOrSelector();
      return;
    }

    // Open the cart's free-gift selector.
    var selectorEl = event.target.closest('.js-open-free-gift-selector');
    if (selectorEl) {
      if (onSlider(event, selectorEl)) {
        return;
      }
      openFreeGiftSelector();
    }
  });
})();
